/**
 * Identidade de contato WhatsApp (BSUID / usernames) — ADR-0016 / Fase 5.
 *
 * Fonte única de verdade para derivar o ESTADO de identidade que a UI exibe.
 * A UI nunca vê o BSUID cru (`CC.xxx`); só o estado + `username`/telefone.
 *
 * Estados:
 *   - "phone"    → telefone conhecido (exibe o número).
 *   - "hidden"   → telefone oculto, sem username conhecido ("Via username").
 *   - "username" → telefone oculto, com username conhecido ("@handle").
 */

export const IDENTITY_STATES = ["phone", "hidden", "username"];

/**
 * Formato de BSUID (ADR-0016): duas letras ISO + ponto (casa também a variante
 * ENT). Usado como guarda defensiva: qualquer string que case isto é um
 * identificador cru e NUNCA pode ir para a tela.
 */
export const BSUID_REGEX = /^[A-Z]{2}\./;

/**
 * True quando `value` parece um BSUID cru (não deve ser renderizado).
 * @param {unknown} value
 * @returns {boolean}
 */
export function looksLikeBsuid(value) {
  return typeof value === "string" && BSUID_REGEX.test(value.trim());
}

/**
 * Remove `@` opcional e impede que um BSUID cru seja tratado como username.
 * @param {unknown} value
 * @returns {string}
 */
export function getSafeUsername(value) {
  if (typeof value !== "string") return "";
  const normalized = value.trim().replace(/^@+/, "");
  if (!normalized || looksLikeBsuid(normalized)) return "";
  return normalized;
}

/**
 * Telefone seguro para renderização. Se um backend antigo preencher `phone`
 * com BSUID, a UI trata como contato oculto em vez de formatar dígitos soltos.
 * @param {unknown} value
 * @returns {string}
 */
export function getSafePhone(value) {
  if (value == null) return "";
  const normalized = String(value).trim();
  if (!normalized || looksLikeBsuid(normalized)) return "";
  return normalized;
}

/**
 * Nome seguro para renderização final. Nomes técnicos/BSUID viram fallback.
 * @param {unknown} value
 * @param {string} fallback
 * @returns {string}
 */
export function getSafeContactName(value, fallback = "Contato desconhecido") {
  if (value == null) return fallback;
  const normalized = String(value).trim();
  if (!normalized || looksLikeBsuid(normalized)) return fallback;
  return normalized;
}

/**
 * Resolve o estado de identidade de um contato/sessão a partir dos campos
 * aditivos do backend. Degrada com segurança quando `identityState` está
 * ausente (backend antigo em dev): telefone presente → "phone"; senão "hidden".
 *
 * @param {object} entity
 * @param {"phone"|"hidden"|"username"} [entity.identityState]
 * @param {boolean} [entity.hasHiddenPhone]
 * @param {string|null} [entity.phone]   telefone (ou phoneNumber) — passe o que existir
 * @param {string|null} [entity.username]
 * @returns {"phone"|"hidden"|"username"}
 */
export function resolveIdentityState({
  identityState,
  hasHiddenPhone,
  phone,
  username,
} = {}) {
  const safePhone = getSafePhone(phone);
  const safeUsername = getSafeUsername(username);

  if (identityState === "phone") {
    if (safePhone) return "phone";
    return safeUsername ? "username" : "hidden";
  }
  if (identityState === "username") {
    if (safeUsername) return "username";
    return safePhone ? "phone" : "hidden";
  }
  if (identityState === "hidden") return "hidden";

  // Degradação (backend antigo — campos aditivos ausentes).
  if (safePhone && !hasHiddenPhone) return "phone";
  if (safeUsername) return "username";
  if (hasHiddenPhone) return "hidden";
  return "hidden";
}
